/**
 * Turns a feedback attachment API path into a short-lived signed blob URL.
 *
 * GraphQL hands out paths on our own API (`/api/feedback/attachments/{id}`),
 * which require a bearer token. An <img> or <video> element cannot send one, so
 * the token is spent here on a fetch and only the signed URL that comes back is
 * given to the element. The authorisation check therefore still runs once per
 * mint rather than being skipped by a URL embedded in the GraphQL payload.
 */

import { getAuthToken } from "@/lib/getAuthToken";

// Comfortably inside the 600 s image SAS lifetime, so a cached URL is never
// handed to an element close to its expiry.
const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  url: string;
  mintedAt: number;
}

const cache = new Map<string, CacheEntry>();
// Deduplicates concurrent callers: a report with five attachments mounts five
// components at once, and two of them may share a path.
const inFlight = new Map<string, Promise<string>>();

export function forgetAttachmentUrl(path: string): void {
  cache.delete(path);
}

export async function resolveAttachmentUrl(path: string): Promise<string> {
  const cached = cache.get(path);
  if (cached && Date.now() - cached.mintedAt < CACHE_TTL_MS) return cached.url;

  const pending = inFlight.get(path);
  if (pending) return pending;

  const request = mint(path).finally(() => inFlight.delete(path));
  inFlight.set(path, request);
  return request;
}

async function mint(path: string): Promise<string> {
  const token = await getAuthToken();
  const response = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`Liitteen osoitteen haku epäonnistui (${response.status})`);
  }

  const body = (await response.json()) as { url?: string };
  if (!body.url) throw new Error("Liitteen osoite puuttui vastauksesta");

  cache.set(path, { url: body.url, mintedAt: Date.now() });
  return body.url;
}
